import { useState, useRef, useMemo } from "react";
import VehicleChildForm from "./components/vehicleChildForm";
import DriverChildForm from "./components/driverChildForm";
import DealershipChildForm from "./components/dealershipChildForm";
import { requiredFieldsByStep } from "./utils/required-fields-by-step";
import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import { IForm } from "../..";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import ServiceChildForm from "./components/serviceChildForm";
import { createServiceBaySchedule } from "../../../../api/serviceBay";
import { scrollToFirstMissingField } from "./utils/scroll-to-missing-field";
import { OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../../../../enums/occurrenceStep";
import { uploadFile } from "../../../../api/fetchFiles";

const NewOccurrenceForm = (props: IForm) => {

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const formContainerRef = useRef<HTMLDivElement>(null);

  const occurrenceType = props.occurrence?.occurrenceType;
  const isRoadAssistance = occurrenceType === 'A' || occurrenceType === 'W';
  const isDealership = occurrenceType === 'D';

  const callRequiredFields = useMemo(() => {
    if (isDealership) {
      const dealershipAllowed = [
        'vehicleOdometer', 'vehicleLicensePlate', 'vehicleModel',
        'dealershipDn', 'emissionStandard', 'country', 'source',
      ];
      return requiredFieldsByStep.CALL.filter(f => dealershipAllowed.includes(f));
    }
    return requiredFieldsByStep.CALL.filter((f) => {
      if (isRoadAssistance && f === 'driverName') return false;
      if (f === 'vehicleStopped') return false;
      if (f === 'occurrenceType') return false;
      return true;
    });
  }, [occurrenceType]);

  // TASK 26658: Adicionar o dealershipDn como campo opcional para novos chamados para ocorrências do tipo Guincho ou Socorro
  const isDealershipDnOptional = ['W', 'A'].includes(props.occurrence?.occurrenceType ?? '');

  const driverRequiredFields = useMemo(() => {
    if (isRoadAssistance || isDealership) return [];
    return requiredFieldsByStep.DRIVER;
  }, [occurrenceType]);

  const serviceHiddenFields = useMemo(() => {
    const base = [
      'estimatedTime',
      'partOrderStatus',
      'osNumber',
      'osDtOpenAt',
      'driverCheckInDriver',
      'mechanicLocation',
      'towTruckLocation',
      'literatureDiagnosis',
      'literatureTroubleshooting',
      'tasNumber',
      'tasStatus',
      'checklist',
      'solutionProposed',
      "vehicleExitAt",
    ];
    if (isDealership) {
      return [
        ...base,
        'criticality',
        'createdBy',
        'dtStart',
        'dtEnd',
        'modules',
        'estimateTimeRepair',
        'report',
        'vehicleStopped',
        'hasLink',
        'mainOccurrence',
        'vehiclePayloadType',
        'vehicleMaximumPayload',
        'vehicleCriticalPayload',
        "vehicleExitAt",
      ];
    }
    return base;
  }, [occurrenceType]);

  return (
    <div ref={formContainerRef}>
      <VehicleChildForm
        occurrence={props.occurrence}
        setOccurrence={props.setOccurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[]}
        disabledFields={[]}
        requiredFields={requiredFieldsByStep.VEHICLE}
        disabledAllFields={props.disabledAllFields}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      {!isDealership && (
        <DriverChildForm
          occurrence={props.occurrence}
          setOccurrenceValue={props.setOccurrenceValue}
          hiddenFields={[]}
          disabledFields={[]}
          requiredFields={driverRequiredFields}
          disabledAllFields={props.disabledAllFields}
          setOccurrenceStepValue={props.setOccurrenceStepValue}
          hasError={hasError}
        />
      )}

      <DealershipChildForm
        occurrence={props.occurrence}
        setOccurrence={props.setOccurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[]}
        disabledFields={[
          'dealershipDn',
        ]}
        requiredFields={isDealershipDnOptional ? [] : ['dealershipDn']}
        disabledAllFields={props.disabledAllFields}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        persona={props.persona}
      />

      <ServiceChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={serviceHiddenFields}
        disabledFields={['createdBy', 'dtStart', 'dtEnd']}
        requiredFields={callRequiredFields}
        step={props.step}
        disabledAllFields={props.disabledAllFields}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        partners={props.partners}
        upsertDispatch={props.upsertDispatch}
        cancelDispatch={props.cancelDispatch}
        removeDispatch={props.removeDispatch}
        hasError={hasError}
        persona={props.persona}
        scheduling={props.scheduling}
        pendingFiles={pendingFiles}
        onPickPending={(files) => setPendingFiles(prev => [...prev, ...files])}
        onRemovePending={(name) => setPendingFiles(prev => prev.filter(f => f.name !== name))}
      />

      {(props.persona !== 'MANAGER' && !props.finalized) && !props.hiddenSaveButton && <div className="flex justify-end margin-top-20">
        <button
          className={`btn btn-primary ${props.loading ? 'btn-loading disabled' : ''}`}
          type="button"
          onClick={async () => {
            if (!props.occurrence || !props.create) return;
            if (
              !props.isValidStep(
                [...callRequiredFields, isDealershipDnOptional ? '' : 'dealershipDn'].filter(e => e !== ''), 
                props.occurrence
              )
            ) {
              setHasError(true);
              const container = formContainerRef.current?.closest('.kanban-sidebar') as HTMLElement;
              scrollToFirstMissingField(callRequiredFields, props.occurrence, container);
              return;
            }
            setHasError(false);

            const callStep = props.occurrence.steps?.find(s => s.name === OccurrenceStepLabelEnum.CALL)
              || props.step;

            const { wasCreated, error, occurrenceId } = await props.create({
              ...props.occurrence,
              steps: [{...callStep, step: OccurrenceStepEnum.CALL, latest: 0 }]
            });
            if (wasCreated) {
              if (props.occurrence.serviceBaySchedule) {
                await createServiceBaySchedule({
                  ...props.occurrence.serviceBaySchedule,
                  occurrence: props.occurrence.serviceBaySchedule.occurrence?.id,
                  serviceBay: props.occurrence.serviceBaySchedule.serviceBay?.id,
                });
              }
              if (occurrenceId && pendingFiles.length > 0) {
                uploadFile({ path: `kanban/${occurrenceId}`, file: pendingFiles });
              }
              setShowSuccessDialog(true);
            } else {
              setErrorMessage(error || 'Ocorreu um erro ao criar o chamado. Por favor, tente novamente ou entre em contato com o suporte.');
              setShowErrorDialog(true);
            }
          }}
          disabled={props.loading || props.disabledAllFields}
        >Criar chamado</button>
      </div>}

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Erro ao criar chamado"
        body={errorMessage}
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Chamado criado com sucesso"
        body="O chamado foi criado com sucesso."
      />
    </div>
  );
};

export default NewOccurrenceForm;
